/**
 * useAgentPresence – presencia en vivo de los agentes («estilo AnyDesk»).
 *
 * El backend mantiene un registro de los sockets de agente abiertos y difunde
 * `agent:presence` a la room de la empresa. Este hook:
 *
 *   1. pide una foto inicial (`GET /agent/presence`) para no arrancar a ciegas,
 *   2. aplica los eventos que llegan por Socket.IO en tiempo real y,
 *   3. comparte el mapa entre todos los componentes: un listado con 30 badges
 *      no dispara 30 peticiones.
 *
 * Si el WebSocket está bloqueado, la foto se refresca cada 30 s y el mapa sigue
 * cuadrando; si no hay ni foto ni evento, el componente cae al `agent_status`
 * guardado en la base de datos, que es lo que había antes.
 */

import { useEffect, useReducer } from 'react';
import api from '../services/api';
import { useSocket } from '../contexts/SocketContext';

const EMPTY = Object.freeze({});
const SNAPSHOT_TTL_MS = 15000;
const SNAPSHOT_REFRESH_MS = 30000;

const maps = new Map();       // company_id -> { agent_id: { online, transport, ts } }
const fetchedAt = new Map();  // company_id -> timestamp
const inflight = new Map();   // company_id -> Promise
const listeners = new Set();

function notify() {
  listeners.forEach((listener) => listener());
}

function normalizeCompanyId(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

async function ensureSnapshot(companyId, { force = false } = {}) {
  const cid = normalizeCompanyId(companyId);
  if (cid === null) return;
  if (!force) {
    const last = fetchedAt.get(cid) || 0;
    if (Date.now() - last < SNAPSHOT_TTL_MS) return;
  }
  if (inflight.has(cid)) return;
  const request = api
    .getAgentPresence(cid)
    .then((payload) => {
      const next = {};
      (payload?.agents || []).forEach((agent) => {
        if (!agent?.agent_id) return;
        next[agent.agent_id] = {
          online: Boolean(agent.online),
          transport: agent.transport || null,
          ts: agent.ts || null,
        };
      });
      maps.set(cid, next);
      fetchedAt.set(cid, Date.now());
      notify();
    })
    .catch(() => {
      // Silencio: el `agent_status` de la base de datos sigue siendo válido.
    })
    .finally(() => {
      inflight.delete(cid);
    });
  inflight.set(cid, request);
}

function applyPresence(payload, companyIdHint) {
  if (!payload?.agent_id) return false;
  const cid = normalizeCompanyId(payload.company_id ?? companyIdHint);
  if (cid === null) return false;
  const map = { ...(maps.get(cid) || {}) };
  map[payload.agent_id] = {
    online: Boolean(payload.online),
    transport: payload.transport || null,
    ts: payload.ts || null,
  };
  maps.set(cid, map);
  notify();
  return true;
}

/** Devuelve `{ [agent_id]: { online, transport, ts } }` para la empresa dada. */
export default function useAgentPresence(companyId) {
  const socket = useSocket();
  const [, forceRender] = useReducer((n) => n + 1, 0);

  useEffect(() => {
    listeners.add(forceRender);
    return () => listeners.delete(forceRender);
  }, []);

  useEffect(() => {
    const cid = normalizeCompanyId(companyId);
    if (cid === null) return undefined;
    ensureSnapshot(cid);
    const timer = window.setInterval(
      () => ensureSnapshot(cid, { force: true }),
      SNAPSHOT_REFRESH_MS,
    );
    return () => window.clearInterval(timer);
  }, [companyId]);

  useEffect(() => {
    if (!socket) return undefined;
    const onPresence = (payload) => applyPresence(payload, companyId);
    const onConnect = () => ensureSnapshot(companyId, { force: true });
    socket.on('agent:presence', onPresence);
    socket.on('connect', onConnect);
    return () => {
      socket.off('agent:presence', onPresence);
      socket.off('connect', onConnect);
    };
  }, [socket, companyId]);

  const cid = normalizeCompanyId(companyId);
  if (cid === null) return EMPTY;
  return maps.get(cid) || EMPTY;
}

/** Estado efectivo: la presencia manda sobre el `agent_status` guardado. */
export function resolveAgentStatus(status, presence) {
  if (!presence) return status || 'unknown';
  return presence.online ? 'online' : 'offline';
}
